import {
  createContext,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { FormConfig } from "../api/types/form";
import { FormErrors, FormValues } from "../components/form/types";
import NewForm from "../components/form/fields";
import { buildValueObj } from "../utils/form";
import { useDidMountEffect } from "../utils/mount";
import { usePrevious } from "@uidotdev/usehooks";
import { makeHash } from "../utils/static";
import TranslateDialog, {
  TranslateDialogRef
} from "../components/dialog/TranslateDialog";

export type FormContextType = {
  config?: FormConfig;
  values?: FormValues;
  errors?: FormErrors;
  updateValue: (key: string, value: any) => void;
  hideAllBlocks: () => void;
  showBlocks: (blockNames: Array<string>, hideBefore?: boolean) => void;
};

export type FormResetRef = () => void;

type FormContextProviderProps = {
  formIndex?: number;
  config: FormConfig;
  errors?: FormErrors;
  children?: ReactElement;
  onChange?: (newValues: any) => void;
  onSubmit?: () => void;
  resetRef?: MutableRefObject<FormResetRef | undefined>;
  formContent?: ReactNode | null;
  passedLocale?: string;
  nested?: boolean
};

const defaultContext: FormContextType = {
  config: undefined,
  values: undefined,
  errors: undefined,
  updateValue: () => {
  },
  hideAllBlocks: () => {
  },
  showBlocks: () => {
  }
};

const FormContext = createContext<FormContextType>(defaultContext);

export const useFormValues = (name: string) => {
  const { values, updateValue } = useContext(FormContext);
  const selectedValue = values?.[name] ?? undefined;

  return useMemo(() => {
    return {
      value: selectedValue,
      onChange: (newValue: any) => updateValue(name, newValue)
    };
  }, [selectedValue]);
};

export const useFormValueMatcher = (matcher: Array<string>) => {
  const { values, updateValue } = useContext(FormContext);
  const selectedValues: FormValues = {};
  for (const inputField of matcher) {
    selectedValues[inputField] = values?.[inputField] ?? null;
  }

  return useMemo(() => {
    return {
      values: selectedValues,
      onChange: (newValues: { [input: string]: any }) => {
        for (const fieldName of Object.keys(newValues ?? {})) {
          updateValue(fieldName, newValues[fieldName]);
        }
      }
    };
  }, [selectedValues]);
};

export const useFormErrors = (name: string) => {
  const { errors } = useContext(FormContext);
  const selectedError = errors?.[name] ?? undefined;

  return useMemo(() => {
    return {
      error: selectedError
    };
  }, [selectedError]);
};

export const useFormBlocks = () => {
  const { config, hideAllBlocks, showBlocks } = useContext(FormContext);

  return useMemo(() => {
    return {
      hideAllBlocks,
      showBlocks
    };
  }, [config]);
};

export default function FormContextProvider({
                                              formIndex,
                                              config,
                                              errors,
                                              onChange,
                                              onSubmit,
                                              resetRef,
                                              formContent,
                                              passedLocale,
                                              nested = false
                                            }: FormContextProviderProps) {
  const translateDialogRef = useRef<TranslateDialogRef>();

  const [values, setValues] = useState<FormValues>(buildValueObj(config));

  const configHash = makeHash(config);
  const prevConfigHash = usePrevious(configHash);

  const reset = () => {
    setValues(buildValueObj(config));
  };

  useEffect(() => {
    if (configHash !== prevConfigHash) {
      reset();
    }
  }, [configHash]);

  useDidMountEffect(() => {
    if (prevConfigHash !== configHash && onChange) {
      onChange(values);
    }
  }, [values]);

  useEffect(() => {
    if (resetRef) {
      resetRef.current = reset;
    }
  }, [resetRef]);

  const updateValue = (key: string, newValue: any) => {
    setValues(draft => {
      return { ...draft, [key]: newValue };
    });
  };

  const hideAllBlocks = () => {
    for (const blockName of config?.block_names ?? []) {
      const domElement = document.getElementById(
        `block-${blockName}-${formIndex}`
      );
      if (domElement) {
        domElement.style.display = "none";
      }
    }
  };

  const showBlocks = (blockNames: Array<string>, hideBefore = true) => {
    if (hideBefore) {
      hideAllBlocks();
    }
    for (const visibleBlock of blockNames) {
      const domElement = document.getElementById(
        `block-${visibleBlock}-${formIndex}`
      );
      if (domElement) {
        domElement.style.display = "block";
      }
    }
  };

  return useMemo(() => {
    const value = {
      config,
      values,
      errors,
      updateValue,
      hideAllBlocks,
      showBlocks
    };

    return (
      <FormContext.Provider value={value}>
        <NewForm
          formIndex={formIndex}
          fields={config.fields}
          additionalFields={config.additional_fields}
          blockLayout={config.block_layout ?? false}
          blockNames={config.block_names}
          blockSizes={config.block_sizes}
          onSubmit={onSubmit}
          translateDialogRef={translateDialogRef}
          formContent={formContent}
          passedLocale={passedLocale}
          formType={config.type}
          nested={nested}
        />
        <TranslateDialog dialogRef={translateDialogRef} />
      </FormContext.Provider>
    );
  }, [config, values, errors]);
}
